import { spawnSync } from 'node:child_process';
import { writeFileSync, mkdirSync, readdirSync, unlinkSync, chmodSync, existsSync } from 'node:fs';
import { homedir } from 'node:os';
import { join } from 'node:path';
import { forComponent } from '../src/index';
import type { Trigger } from '../src/scheduler';
import { parseCron, nextCronAfter } from '../src/scheduler';
import { readJsonFile } from './util';

const log = forComponent('os-sched');

/**
 * OS-backend scheduler (mind/12 spec): jobs that survive quit/reboot. Each job is a small shell
 * script under ~/.agent/sched/ running `agentx -p "<prompt>" --resume <sessionId> --yes` from the
 * project dir, plus a JSON meta file (the source of truth for list/cancel). Trigger mechanisms:
 *
 *   macOS  — launchd user agent (~/Library/LaunchAgents/cc.livx.agentx.sched-<id>.plist):
 *            {at}/simple-cron → StartCalendarInterval; {everyMs} → StartInterval. One-offs
 *            self-remove (the script calls `launchctl remove` + deletes its own files).
 *   Linux  — recurring → user crontab line tagged `# agentx-sched-<id>`; one-off → `at`.
 *
 * No daemon, no queue: the OS scheduler + session JSON ARE the persistence. Results land in the
 * session transcript (visible on next --resume) + ~/.agent/sched/<id>.log; the agent is told to
 * use PushNotification for out-of-band alerts.
 */

export interface OsJobSpec { id: string; prompt: string; sessionId: string; cwd: string; trigger: Trigger; label?: string }
interface JobMeta extends OsJobSpec { created: number; mechanism: string }

export class OsScheduler {
  public options: OsSchedulerOptions;
  constructor(options?: Partial<OsSchedulerOptions>) {
    this.options = { ...new OsSchedulerOptions(), ...options };
  }

  private get dir(): string { return join(this.options.home, '.agent', 'sched'); }
  private label(id: string): string { return `cc.livx.agentx.sched-${id}`; }
  private plistPath(id: string): string { return join(this.options.home, 'Library', 'LaunchAgents', `${this.label(id)}.plist`); }
  private run(cmd: string, args: string[], input?: string): string {
    return this.options.exec(cmd, args, input);
  }

  available(): boolean {
    return this.options.platform === 'darwin' || this.options.platform === 'linux';
  }

  /** Register the job with the OS. Returns a human description of the mechanism used. Throws on failure. */
  schedule(spec: OsJobSpec): string {
    if (!this.available()) throw new Error(`no OS scheduler on ${this.options.platform}`);
    mkdirSync(this.dir, { recursive: true });
    const oneOff = 'at' in spec.trigger;
    const script = this.writeScript(spec, oneOff);
    const mechanism = this.options.platform === 'darwin' ? this.scheduleDarwin(spec, script) : this.scheduleLinux(spec, script, oneOff);
    const meta: JobMeta = { ...spec, created: Date.now(), mechanism };
    writeFileSync(join(this.dir, `${spec.id}.json`), JSON.stringify(meta, null, 2));
    return mechanism;
  }

  cancel(id: string): boolean {
    const meta = readJsonFile<JobMeta | null>(join(this.dir, `${id}.json`), null);
    if (!meta) return false;
    try {
      if (this.options.platform === 'darwin') {
        try { this.run('launchctl', ['remove', this.label(id)]); } catch { /* not loaded */ }
        try { unlinkSync(this.plistPath(id)); } catch { /* already gone */ }
      } else if (meta.mechanism.startsWith('crontab')) {
        const cur = (() => { try { return this.run('crontab', ['-l']); } catch { return ''; } })();
        const next = cur.split('\n').filter((l) => !l.includes(`# agentx-sched-${id}`)).join('\n');
        this.run('crontab', ['-'], next.trim() ? next.trimEnd() + '\n' : '');
      } else if (meta.mechanism.startsWith('at:')) {
        try { this.run('atrm', [meta.mechanism.slice(3)]); } catch { /* already fired */ }
      }
    } catch (e) { log.debug(`cancel ${id}`, e); }
    for (const f of [`${id}.json`, `${id}.sh`]) { try { unlinkSync(join(this.dir, f)); } catch { /* gone */ } }
    return true;
  }

  list(): JobMeta[] {
    if (!existsSync(this.dir)) return [];
    return readdirSync(this.dir)
      .filter((f) => f.endsWith('.json'))
      .map((f) => readJsonFile<JobMeta | null>(join(this.dir, f), null))
      .filter(Boolean) as JobMeta[];
  }

  /** The per-job runner script: cd to the project, headless-resume the session, log, notify. */
  private writeScript(spec: OsJobSpec, oneOff: boolean): string {
    const p = join(this.dir, `${spec.id}.sh`);
    const q = (s: string) => `'${s.replace(/'/g, `'\\''`)}'`;
    const cleanup = oneOff
      ? this.options.platform === 'darwin'
        ? `launchctl remove ${this.label(spec.id)} 2>/dev/null; rm -f ${q(this.plistPath(spec.id))} ${q(join(this.dir, `${spec.id}.json`))} ${q(p)}\n`
        : `rm -f ${q(join(this.dir, `${spec.id}.json`))} ${q(p)}\n`
      : '';
    writeFileSync(p, `#!/bin/sh
# agentx scheduled job ${spec.id}${spec.label ? ` — ${spec.label}` : ''}
cd ${q(spec.cwd)} || exit 1
${this.options.agentx} -p ${q(spec.prompt)} --resume ${q(spec.sessionId)} --yes >> ${q(join(this.dir, `${spec.id}.log`))} 2>&1
${cleanup}`);
    chmodSync(p, 0o755);
    return p;
  }

  private scheduleDarwin(spec: OsJobSpec, script: string): string {
    const t = spec.trigger;
    let trigger: string;
    if ('everyMs' in t) {
      trigger = `<key>StartInterval</key><integer>${Math.max(60, Math.round(t.everyMs / 1000))}</integer>`;
    } else if ('at' in t) {
      const d = new Date(t.at);
      trigger = `<key>StartCalendarInterval</key><dict><key>Minute</key><integer>${d.getMinutes()}</integer><key>Hour</key><integer>${d.getHours()}</integer><key>Day</key><integer>${d.getDate()}</integer><key>Month</key><integer>${d.getMonth() + 1}</integer></dict>`;
    } else {
      // cron → StartCalendarInterval supports one value per field; ranges/lists/steps would explode
      // into dict products — keep it honest and reject those (use {everyMs} or simple crons).
      const f = parseCron(t.cron);
      const dict: string[] = [];
      const put = (key: string, vals: number[], full: number) => {
        if (vals.length === full) return; // '*' — omit (launchd wildcard)
        if (vals.length !== 1) throw new Error(`macOS launchd supports only single-value cron fields (got "${t.cron}") — use {everyMs} or a simpler cron`);
        dict.push(`<key>${key}</key><integer>${vals[0]}</integer>`);
      };
      put('Minute', f.minute, 60); put('Hour', f.hour, 24); put('Day', f.dom, 31); put('Month', f.month, 12); put('Weekday', f.dow, 7);
      trigger = `<key>StartCalendarInterval</key><dict>${dict.join('')}</dict>`;
    }
    const plist = `<?xml version="1.0" encoding="UTF-8"?>
<!DOCTYPE plist PUBLIC "-//Apple//DTD PLIST 1.0//EN" "http://www.apple.com/DTDs/PropertyList-1.0.dtd">
<plist version="1.0"><dict>
<key>Label</key><string>${this.label(spec.id)}</string>
<key>ProgramArguments</key><array><string>/bin/sh</string><string>${script}</string></array>
${trigger}
<key>RunAtLoad</key><false/>
</dict></plist>
`;
    mkdirSync(join(this.options.home, 'Library', 'LaunchAgents'), { recursive: true });
    writeFileSync(this.plistPath(spec.id), plist);
    this.run('launchctl', ['load', this.plistPath(spec.id)]);
    return `launchd:${this.label(spec.id)}`;
  }

  private scheduleLinux(spec: OsJobSpec, script: string, oneOff: boolean): string {
    const t = spec.trigger;
    if (oneOff) {
      // `at` prints "job N at <date>" on stderr
      const d = new Date(('at' in t ? t.at : Date.now()));
      const stamp = `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')} ${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
      const out = this.run('at', [stamp], `/bin/sh ${script}\n`);
      const jobId = /job (\d+)/.exec(out)?.[1] ?? '';
      return `at:${jobId}`;
    }
    const expr = 'cron' in t ? t.cron : `*/${Math.max(1, Math.round((t as { everyMs: number }).everyMs / 60_000))} * * * *`;
    parseCron(expr); // validate before touching the crontab
    const cur = (() => { try { return this.run('crontab', ['-l']); } catch { return ''; } })();
    this.run('crontab', ['-'], `${cur.trimEnd()}\n${expr} /bin/sh ${script} # agentx-sched-${spec.id}\n`.replace(/^\n/, ''));
    return `crontab:${expr}`;
  }
}

export class OsSchedulerOptions {
  platform: NodeJS.Platform = process.platform;
  home = homedir();
  /** How the fired job invokes the CLI — a RAW shell snippet (may be `bun /path/cli.ts`). Default: `agentx` on PATH. */
  agentx = 'agentx';
  /** Injectable executor for tests. Returns stdout+stderr merged — `at` reports "job N" on STDERR,
   *  and the job id is what atrm needs for cancel. Throws on non-zero exit. */
  exec: (cmd: string, args: string[], input?: string) => string = (cmd, args, input) => {
    const r = spawnSync(cmd, args, { input, encoding: 'utf8' });
    if (r.error) throw r.error;
    if (r.status !== 0) throw new Error(`${cmd} exited ${r.status}: ${r.stderr || r.stdout}`);
    return `${r.stdout ?? ''}${r.stderr ?? ''}`;
  };
}

/** Spec routing heuristic: one-off ≥30min out → OS (survives quit); near one-offs and ALL
 *  recurring triggers stay in-process unless the agent explicitly asks for the OS backend
 *  (a recurring OS job spawns repeated headless runs — that should be a deliberate choice). */
export function routeTrigger(trigger: Trigger, backendHint: string | undefined, now = Date.now()): 'session' | 'os' {
  if (backendHint === 'os') return 'os';
  if (backendHint === 'session') return 'session';
  return 'at' in trigger && trigger.at - now >= 30 * 60_000 ? 'os' : 'session';
}

/** Next-fire preview for display (mirrors Scheduler.nextFire for an unsaved trigger). */
export function nextFireOf(trigger: Trigger, now = Date.now()): number | null {
  if ('at' in trigger) return trigger.at;
  if ('everyMs' in trigger) return now + trigger.everyMs;
  return nextCronAfter(trigger.cron, now);
}
